'use client'

import { SiJavascript, SiJson, SiMarkdown, SiReact, SiTypescript } from '@icons-pack/react-simple-icons'
import { DynamicCodeBlock } from 'fumadocs-ui/components/dynamic-codeblock'
import { FolderIcon, FolderOpenIcon } from 'lucide-react'
import { cn } from '~/lib/utils'
import { FeatureCard } from './feature-card'

interface LiveCodingTasksProps {
  className?: string
}

const mockCode = `import React from 'react';
import { cn } from '@/lib/utils';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  onClick,
  className,
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

  const variants = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'border border-input hover:bg-accent hover:text-accent-foreground',
  };

  const sizes = {
    sm: 'h-9 px-3 text-sm',
    md: 'h-10 px-4 py-2',
    lg: 'h-11 px-8 text-lg',
  };

  return (
    <button
      className={cn(
        baseStyles,
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export default Button;`

const projectStructure = [
  {
    name: 'src',
    icon: FolderOpenIcon,
    isFolder: true,
    children: [
      {
        name: 'components',
        icon: FolderOpenIcon,
        isFolder: true,
        children: [
          { name: 'Button.tsx', icon: SiReact, isFolder: false },
          { name: 'Modal.tsx', icon: SiReact, isFolder: false },
          { name: 'Card.tsx', icon: SiReact, isFolder: false },
        ],
      },
      {
        name: 'pages',
        icon: FolderOpenIcon,
        isFolder: true,
        children: [
          { name: 'Home.tsx', icon: SiReact, isFolder: false },
          { name: 'About.tsx', icon: SiReact, isFolder: false },
        ],
      },
      {
        name: 'hooks',
        icon: FolderOpenIcon,
        isFolder: true,
        children: [
          { name: 'useAuth.ts', icon: SiTypescript, isFolder: false },
          { name: 'useLocalStorage.ts', icon: SiTypescript, isFolder: false },
        ],
      },
      { name: 'App.tsx', icon: SiReact, isFolder: false },
      { name: 'index.tsx', icon: SiReact, isFolder: false },
      { name: 'types.ts', icon: SiTypescript, isFolder: false },
    ],
  },
  {
    name: 'public',
    icon: FolderIcon,
    isFolder: true,
    children: [
      { name: 'favicon.ico', icon: SiJavascript, isFolder: false },
      { name: 'logo.svg', icon: SiJavascript, isFolder: false },
    ],
  },
  { name: 'package.json', icon: SiJson, isFolder: false },
  { name: 'tsconfig.json', icon: SiJson, isFolder: false },
  { name: 'tailwind.config.js', icon: SiJavascript, isFolder: false },
  { name: 'README.md', icon: SiMarkdown, isFolder: false },
]

function FileItem({
  name,
  icon: Icon,
  className,
  isFolder = false,
  level = 0,
}: {
  name: string
  icon: typeof FolderIcon
  className?: string
  isFolder?: boolean
  level?: number
}) {
  return (
    <div
      className={cn(`flex items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-accent/50`, className)}
      style={{ paddingLeft: `${8 + level * 16}px` }}
    >
      <Icon className={cn('size-3 shrink-0', isFolder
        ? 'text-blue-500'
        : `text-gray-500`)}
      />
      <p
        className={cn(
          'text-xs font-medium',
          isFolder
            ? `text-blue-600 dark:text-blue-400`
            : `text-gray-700 dark:text-gray-300`,
        )}
      >
        {name}
      </p>
    </div>
  )
}

function Editor() {
  return (
    <div className="max-w-full overflow-x-auto">
      <DynamicCodeBlock
        code={mockCode}
        codeblock={{
          className: 'rounded-none border-none shadow-none bg-transparent max-w-full',
        }}
        lang="tsx"
      />
    </div>
  )
}

function renderFileTree(items: any[], level = 0) {
  return items.map(item => (
    <div key={item.name}>
      <FileItem name={item.name} icon={item.icon} isFolder={item.isFolder} level={level} />
      {item.children && renderFileTree(item.children, level + 1)}
    </div>
  ))
}

function Sidebar() {
  return (
    <div className="flex flex-col gap-1 p-2 font-mono text-xs text-muted-foreground">
      <div className="flex flex-col gap-px">{renderFileTree(projectStructure)}</div>
    </div>
  )
}

function LiveCodingBackground() {
  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Desktop: 3 columns */}
      <div className={`
        hidden aspect-video grid-cols-[180px_1fr_200px] divide-x overflow-hidden bg-gradient-to-b to-transparent
        lg:grid
      `}
      >
        <Sidebar />
        <div className="flex flex-col">
          <div className="flex h-8 items-center gap-2 border-b border-gray-200 px-3 dark:border-gray-700">
            <div className="flex gap-1">
              <div className="h-2 w-2 rounded-full bg-red-400" />
              <div className="h-2 w-2 rounded-full bg-yellow-400" />
              <div className="h-2 w-2 rounded-full bg-green-400" />
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Button.tsx
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <Editor />
          </div>
        </div>
        <div className="p-4">
          <div className="mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">
            Terminal
          </div>
          <div className="space-y-1 font-mono text-xs">
            <div className="text-blue-600 dark:text-blue-400">
              $ npm run build
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              ✓ Building for production...
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              ✓ Compiled successfully
            </div>
            <div className="text-green-600 dark:text-green-400">
              ✓ 15 modules transformed
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              ✓ Build completed in 2.3s
            </div>
            <div className="text-yellow-600 dark:text-yellow-400">
              ⚠ 2 warnings
            </div>
            <div className="text-gray-500 dark:text-gray-500">
              {' '}
              • Unused variable 'unusedVar'
            </div>
            <div className="text-gray-500 dark:text-gray-500">
              {' '}
              • Missing dependency in useEffect
            </div>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet: 1 column */}
      <div className={`
        grid aspect-video grid-cols-[1fr_200px] divide-x overflow-hidden bg-gradient-to-b to-transparent
        lg:hidden
      `}
      >
        <div className="flex flex-col">
          <div className="flex h-8 items-center gap-2 border-b border-gray-200 px-3 dark:border-gray-700">
            <div className="flex gap-1">
              <div className="h-2 w-2 rounded-full bg-red-400" />
              <div className="h-2 w-2 rounded-full bg-yellow-400" />
              <div className="h-2 w-2 rounded-full bg-green-400" />
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Button.tsx
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <Editor />
          </div>
        </div>
      </div>

      <div className={`
        absolute inset-0 z-1 size-full bg-gradient-to-t from-white from-5% to-transparent
        dark:from-black/80 dark:via-black/20
      `}
      />
    </div>
  )
}

export function LiveCodingTasks(props: LiveCodingTasksProps) {
  return (
    <FeatureCard
      background={<LiveCodingBackground />}
      title="Лайвкодинг задачи"
      description="Реальные задачи с собеседований, подробные разборы решений и практические примеры от разработчиков."
      className={props.className}
    />
  )
}
